import React from "react";
import useAkiyaSearchHook from "./useAkiyaSearchHook";
import HeadingForAll from "../HeadingForAll/HeadingForAll";
import HeadingContainer from "./HeadingContainerAS";
import CustomButt from "../CustomButton/CustomButt";
import CustomDrop from "../customDropDown/CustomDrop";
import AkiyaCard from "../AkiyaCard/AkiyaCard";
import CardSlide from "../cardSlider/CardSlide";
import QuestionBox from "../QuestionBox/QuestionBox";
import Loader from "../Loader/Loader";
import useWindowDimensions from "../useWindowDimensions";
import SpecialListings from "./SpecialListings";

const japanMap = "/images/akiyaSearch/jp.svg";
const kominkaImages = "/images/akiyaSearch/kominkaImage.webp";
const favImage = "/images/akiyaSearch/favImage.avif";
const pinMap = "/images/akiyaSearch/pinmap.webp";

// Fixed Grid Component - consistent responsive spacing
const Grid2x2 = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 w-full px-4 sm:px-6 md:px-8 lg:px-10 max-w-full mx-auto">
    {children}
  </div>
);

// Grid Item Component - fixed padding consistency
const GridItem = ({ 
  image, 
  alt, 
  headingData, 
  headingIndex, 
  onClick, 
  buttonText, 
  showButton = true,
  isSmallScreen,
  compact = false,
  children 
}) => {
  const getContainerClasses = () => {
    const baseClasses = "bg-white overflow-hidden transition-all duration-300 cursor-pointer transform";
    const desktopClasses = "md:rounded-lg md:shadow-sm md:hover:shadow-md md:hover:scale-[1.02]";
    const mobileClasses = compact && isSmallScreen 
      ? "rounded-none shadow-none hover:shadow-none hover:scale-100"
      : "rounded-lg shadow-sm hover:shadow-md hover:scale-[1.02]";
    
    return `${baseClasses} ${mobileClasses} ${desktopClasses}`;
  };

  return (
    <div 
      className={getContainerClasses()}
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: 'auto'
      }}
    >
      <div style={{ flex: 1 }}>
        <img 
          src={image} 
          alt={alt} 
          className="w-full object-cover"
          style={{ 
            height: 'clamp(14rem, 18vw, 18rem)', // Responsive height
            width: '100%'
          }}
        />
        <div className="p-3 sm:p-4"> {/* Consistent responsive padding */}
          {headingData && headingData[headingIndex] && (
            <HeadingContainer data={headingData[headingIndex]} />
          )}
        </div>
      </div>
      
      {children}
      
      {showButton && !isSmallScreen && buttonText && (
        <div className="opacity-0 md:opacity-100 flex justify-center md:justify-start p-3 sm:p-4 pt-0">
          <CustomButt
            handleButtonClick={(e) => {
              e.stopPropagation();
              onClick && onClick();
            }}
            text={buttonText}
          />
        </div>
      )}
    </div>
  );
};

// Special Grid Item - fixed spacing consistency
const JapanMapGridItem = ({ 
  image, 
  alt, 
  headingData, 
  headingIndex, 
  allPrefectures,
  somePrefectures,
  isSmallScreen,
  compact = false
}) => {
  const getContainerClasses = () => {
    const baseClasses = "bg-white transition-shadow duration-300";
    const desktopClasses = "md:rounded-lg md:shadow-sm md:hover:shadow-md";
    const mobileClasses = compact && isSmallScreen 
      ? "rounded-none shadow-none hover:shadow-none"
      : "rounded-lg shadow-sm hover:shadow-md";
    
    return `${baseClasses} ${mobileClasses} ${desktopClasses}`;
  };

  return (
    <div 
      className={getContainerClasses()}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: 'auto',
        overflow: 'visible'
      }}
    >
      {headingData && headingData[headingIndex] && (
        <div style={{ flex: 1 }}>
          <div className={`overflow-hidden ${compact && isSmallScreen ? 'rounded-none' : 'rounded-t-lg'} ${!compact || !isSmallScreen ? 'md:rounded-t-lg' : ''}`}>
            <img 
              src={image} 
              alt={alt} 
              className="w-full object-cover"
              style={{ 
                height: 'clamp(14rem, 18vw, 18rem)', // Consistent with GridItem
                width: '100%'
              }}
            />
            <div className="p-3 sm:p-4"> {/* Consistent with GridItem */}
              <HeadingContainer data={headingData[headingIndex]} />
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-center md:justify-start p-3 sm:p-4 pt-0 relative z-10"> {/* Consistent padding */}
        {allPrefectures && (
          <CustomDrop
            options={allPrefectures}
            highlightPrefectures={somePrefectures}
          />
        )}
      </div>
    </div>
  );
};

const AkiyaSearch = () => {
  const {
    headingForAllData,
    headingData,
    allPrefectures,
    listingData,
    selectedAkiya,
    handleFavoriteClick,
    handleKominkaClick,
    handlePinClick
  } = useAkiyaSearchHook();
  const { width } = useWindowDimensions();
  const isSmallScreen = width <= 768;

  const somePrefectures = [
    "Ishikawa", "Kyoto", "Tokyo", "Osaka", "Hiroshima", "Chiba",
    "Fukuoka", "Shizuoka", "Miyagi", "Nagano", "Mie", "Niigata",
  ];

  return (
    <div className="w-full min-h-screen">
      {/* Main Heading - consistent spacing */}
      <div className="w-full">
      <HeadingForAll
        smallHeading={headingForAllData.subheading}
        bigHeading={headingForAllData.heading}
      />
      </div>

      <div className="flex justify-center">
        <div className="w-full max-w-[1440px] relative">

          {/* 2x2 Grid Container - improved spacing */}
          <div className="mt-6 sm:mt-8 w-full">
            <Grid2x2>
              <JapanMapGridItem
                image={japanMap}
                alt="japan"
                headingData={headingData}
                headingIndex={0}
                allPrefectures={allPrefectures}
                somePrefectures={somePrefectures}
                isSmallScreen={isSmallScreen}
                compact={true}
              />

              <GridItem
                image={kominkaImages}
                alt="kominka"
                headingData={headingData}
                headingIndex={2}
                onClick={handleKominkaClick}
                buttonText="Check it out!"
                isSmallScreen={isSmallScreen}
              />

              <GridItem
                image={favImage}
                alt="AKIYA2.0"
                headingData={headingData}
                headingIndex={1}
                onClick={handleFavoriteClick}
                buttonText="Your favorites"
                isSmallScreen={isSmallScreen}
              />

              <GridItem
                image={pinMap}
                alt="Pin Map"
                headingData={headingData}
                headingIndex={3}
                onClick={handlePinClick}
                buttonText="Your pins"
                isSmallScreen={isSmallScreen}
              />
            </Grid2x2>
          </div>

          {/* Special Listings Section - responsive spacing */}
          <div className="ml-0 mt-12 sm:mt-16 md:mt-200o mb-2 sm:mb-[10px]">
            {headingData && headingData[3] && (
              <div className="font-bold text-xl sm:text-2xl md:text-[26px]">
                <h1 className="dark-header">Some Listings from AKIYA2.0</h1>
              </div>
            )}
          </div>

          {/* Card Slider Section - consistent spacing */}
          <div className="w-full flex items-center justify-center my-8 sm:my-10">
            <div className="w-[90%] mr-4 sm:mr-[1.5rem] md:mr-[2.5rem] lg:mr-[4rem]">
              {selectedAkiya ? (
                <CardSlide
                  data={selectedAkiya}
                  Card={AkiyaCard}
                  endValues={{
                    xs: 1,
                    sm: 2,
                    md: 3,
                    lg: 5,
                  }}
                />
              ) : (
                <div className="flex justify-center py-8">
                  <Loader big={false} />
                </div>
              )}
            </div>
          </div>

          {/* Special Listings - no extra wrapper spacing */}
          <SpecialListings />

          {/* Question Box - consistent spacing */}
          <div className="px-4 sm:px-6 md:px-8 lg:px-10 mt-8 sm:mt-12">
            <QuestionBox />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AkiyaSearch;